import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Download, Share, X } from "lucide-react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const DISMISS_KEY = "tipomatic_pwa_banner_dismissed_at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 7 dní

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
}

// Add to Home Screen na iOS funguje jen v mobilním Safari.
// Chrome (CriOS), Firefox (FxiOS), Edge (EdgiOS), in-app prohlížeče (FBAN, Instagram…) to neumí.
function isIOSSafari() {
  if (!isIOS()) return false;
  const ua = navigator.userAgent;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser/.test(ua);
  const isInApp = /(FBAN|FBAV|Instagram|Line|MicroMessenger|Snapchat|Pinterest|TikTok)/i.test(ua);
  return isSafari && !isInApp;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isInIframe() {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function recentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export function InstallPWABanner() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [iosHintOpen, setIosHintOpen] = useState(false);
  const iosSafari = isIOSSafari();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone() || isInIframe() || recentlyDismissed()) return;

    // iOS Safari nepodporuje beforeinstallprompt → ukaž banner s návodem.
    // Jiné iOS prohlížeče (Chrome, Firefox, in-app) instalaci neumí → banner neukazujeme.
    if (iosSafari) {
      setVisible(true);
      return;
    }

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [iosSafari]);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const handleInstall = async () => {
    if (deferred) {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      if (choice.outcome === "accepted") {
        setVisible(false);
      } else {
        dismiss();
      }
      return;
    }
    if (iosSafari) setIosHintOpen(true);
  };

  if (!visible) return null;

  return (
    <>
      <div className="fixed inset-x-0 bottom-16 z-50 px-3 md:bottom-4">
        <div className="mx-auto flex max-w-2xl items-center gap-3 rounded-2xl border border-border bg-card/95 p-3 shadow-elevated backdrop-blur">
          <img
            src="/icon-192.png?v=3"
            alt=""
            className="h-10 w-10 shrink-0 rounded-xl object-cover"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">Přidat na plochu</p>
            <p className="text-xs text-muted-foreground">
              {iosSafari
                ? "Otevírej Tipomatic jedním klepnutím jako appku."
                : "Nainstaluj Tipomatic jako appku a otevírej ji jedním klepnutím."}
            </p>
          </div>
          <Button size="sm" onClick={handleInstall} className="shrink-0">
            {iosSafari ? (
              <>
                <Share className="mr-1 h-4 w-4" />
                Jak na to
              </>
            ) : (
              <>
                <Download className="mr-1 h-4 w-4" />
                Instalovat
              </>
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={dismiss}
            aria-label="Zavřít"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Dialog open={iosHintOpen} onOpenChange={setIosHintOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Přidat na plochu</DialogTitle>
            <DialogDescription>Dva kroky v Safari:</DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm">
            <li className="flex items-center gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                1
              </span>
              <span className="flex flex-wrap items-center gap-1">
                Klepni dole na
                <Share className="inline h-4 w-4" aria-label="Sdílet" />
                <strong>Sdílet</strong>
              </span>
            </li>
            <li className="flex items-center gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                2
              </span>
              <span>
                Vyber <strong>Přidat na plochu</strong>
              </span>
            </li>
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
}

